import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import { useStores } from '../../app/StoreContext'
import type { RecipientStore, RecipientSummary } from '../../api/types'
import { Badge, Button, Card, Empty, ErrorBox, Field, Num, Spinner, formatDate, formatDateTime } from '../../ui/primitives'
import { GridHead, GridSummary, useGrid, type ColumnDef } from '../../ui/grid'
import { QuickDates } from './QuickDates'
import { RecipientChart, RecipientTable } from './RecipientChart'

/**
 * Proposte per destinatario, con la finestra sulla data di previsione — la «data richiesta»
 * dell'applicazione attuale — e il dettaglio dei punti vendita che si apre cliccando una barra.
 *
 * <p>Il grafico e la tabella mostrano lo stesso dato: la tabella non è un ripiego, è la vista che
 * serve a chi stampa, a chi legge con uno screen reader e a chi deve copiare i numeri altrove.</p>
 */
export function RecipientPanel() {
  const { storeFilter, storesReady, storeName } = useStores()
  const navigate = useNavigate()

  // Date e destinatario aperto vivono anche nell'URL (pda, pa, dest): chi apre una proposta
  // dal dettaglio e torna indietro ritrova il pannello com'era. Le chiavi delle consegne
  // (da, a, stati, fasce, doc) sono della pagina.
  const [searchParams, setSearchParams] = useSearchParams()

  const [from, setFrom] = useState(searchParams.get('pda') ?? '')
  const [to, setTo] = useState(searchParams.get('pa') ?? '')
  const [view, setView] = useState<'chart' | 'table'>('chart')

  const [rows, setRows] = useState<RecipientSummary[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const [picked, setPicked] = useState<RecipientSummary>()
  const [detail, setDetail] = useState<RecipientStore[]>()
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<unknown>()
  const detailRef = useRef<HTMLDivElement>(null)

  // La griglia dei punti vendita: ordinabile e filtrabile come le altre griglie dell'app.
  const detailColumns = useMemo<ColumnDef<RecipientStore>[]>(
    () => [
      { key: 'store', label: 'Punto vendita', value: (d) => `${d.store} ${storeName(d.store) ?? ''}` },
      { key: 'status', label: 'Stato', value: (d) => (d.confirmed ? 'confermata' : 'da confermare') },
      { key: 'forecastDate', label: 'Data richiesta', value: (d) => d.forecastDate },
      {
        key: 'confirmedAt',
        label: 'Confermata il',
        value: (d) => d.confirmedAt ?? null,
        title: 'Giorno e ora della conferma; vuota finché la proposta è aperta.',
      },
      { key: 'lineCount', label: 'Righe', value: (d) => d.lineCount, numeric: true },
    ],
    [storeName],
  )
  const detailGrid = useGrid(detail ?? [], detailColumns)

  const query = { storeCode: storeFilter, from: from || undefined, to: to || undefined }

  const load = useCallback(() => {
    // Si aspetta l'elenco dei punti vendita: prima il filtro non sa se «tutti» siano tutti.
    if (!storesReady) return
    setLoading(true)
    setError(undefined)
    // Il dettaglio aperto si riferisce alla finestra precedente: chiuderlo evita di mostrare
    // numeri che non corrispondono più al grafico.
    setPicked(undefined)
    setDetail(undefined)

    api
      .get<RecipientSummary[]>('/dashboard/proposals-by-recipient', query)
      .then(setRows)
      .catch(setError)
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeFilter, storesReady, from, to])

  useEffect(load, [load])

  // Stato → URL, senza voci di cronologia in più: il «indietro» deve tornare qui in un colpo.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        const setOrDel = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k))
        setOrDel('pda', from)
        setOrDel('pa', to)
        setOrDel('dest', picked?.recipientCode ?? '')
        return p
      },
      { replace: true },
    )
  }, [from, to, picked, setSearchParams])

  const open = useCallback(
    (recipient: RecipientSummary) => {
      setPicked(recipient)
      setDetail(undefined)
      setDetailError(undefined)
      setDetailLoading(true)

      api
        .get<RecipientStore[]>(
          `/dashboard/proposals-by-recipient/${encodeURIComponent(recipient.recipientCode)}`,
          query,
        )
        .then((d) => {
          setDetail(d)
          detailRef.current?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
          })
        })
        .catch(setDetailError)
        .finally(() => setDetailLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeFilter, from, to],
  )

  // Al primo caricamento, se l'URL nomina un destinatario (si arriva da un «indietro»), il suo
  // dettaglio si riapre da solo. Una volta sola: dopo, chi chiude col bottone deve restare chiuso.
  const restored = useRef(false)
  useEffect(() => {
    if (restored.current || !rows) return
    restored.current = true
    const dest = searchParams.get('dest')
    const target = dest ? rows.find((r) => r.recipientCode === dest) : undefined
    if (target) open(target)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  return (
    <>
      <Card
        title="Proposte per destinatario"
        actions={
          <div className="view-toggle" role="group" aria-label="Vista">
            <Button variant={view === 'chart' ? 'primary' : 'ghost'} onClick={() => setView('chart')}>
              Grafico
            </Button>
            <Button variant={view === 'table' ? 'primary' : 'ghost'} onClick={() => setView('table')}>
              Tabella
            </Button>
          </div>
        }
      >
        <div className="grid filter-grid">
          <Field label="Data richiesta da">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Data richiesta a">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <div className="field field-actions">
            <QuickDates
              from={from}
              to={to}
              allLabel="Sempre"
              onChange={(f, t) => {
                setFrom(f)
                setTo(t)
              }}
            />
          </div>
        </div>

        {loading ?
          <Spinner />
        : error ?
          <ErrorBox error={error} onRetry={load} />
        : view === 'chart' ?
          <RecipientChart rows={rows ?? []} selected={picked?.recipientCode} onSelect={open} />
        : <RecipientTable rows={rows ?? []} onSelect={open} />}
      </Card>

      {picked && (
        <div ref={detailRef}>
          <Card
            title={
              <>
                Punti vendita — {picked.recipientName}{' '}
                <span className="muted">{picked.recipientCode}</span>
              </>
            }
            actions={
              <Button variant="ghost" onClick={() => setPicked(undefined)}>
                Chiudi
              </Button>
            }
          >
            {detailLoading ?
              <Spinner />
            : detailError ?
              <ErrorBox error={detailError} onRetry={() => open(picked)} />
            : !detail || detail.length === 0 ?
              <Empty>Nessun punto vendita per questo destinatario nel periodo.</Empty>
            : <div className="table-wrap">
                <table>
                  <GridHead columns={detailColumns} grid={detailGrid} extraColumns={1} />
                  <tbody>
                    {detailGrid.rows.map((d) => (
                      <tr
                        key={d.proposalId}
                        className="clickable"
                        onClick={() => navigate(`/proposals/${d.proposalId}`)}
                      >
                        <td>
                          <span className="num">{d.store}</span>{' '}
                          <span className="muted">{storeName(d.store)}</span>
                        </td>
                        <td>
                          <Badge tone={d.confirmed ? 'ok' : 'warn'}>
                            {d.confirmed ? 'Confermata' : 'Da confermare'}
                          </Badge>
                        </td>
                        <td>{formatDate(d.forecastDate)}</td>
                        <td>
                          {d.confirmedAt ?
                            formatDateTime(d.confirmedAt)
                          : <span className="muted">—</span>}
                        </td>
                        <td className="num">
                          <Num value={d.lineCount} />
                        </td>
                        <td className="num">
                          <span className="muted">Apri →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <GridSummary grid={detailGrid} total={detail.length} />
              </div>
            }
          </Card>
        </div>
      )}
    </>
  )
}
